<template>
  <div>
    <div class="grid grid-cols-[1fr_336px] gap-6 md:grid-cols-1">
      <div class="flex-1">
        <AppCard
          :mode="mode"
          title="Search"
          :isSmallTitle="true"
          :classes="'h-full md:h-auto'"
        >
          <template #body>
            <div class="flex justify-between gap-3 md:flex-col md:gap-4">
              <AppInput
                :mode="mode"
                id="query"
                title="Search query"
                placeholder="Enter keyword"
                v-model="query"
                parentClasses="flex-1"
              />
              <AppInput
                :mode="mode"
                id="days"
                type="number"
                title="Days back"
                min="0"
                placeholder="30"
                v-model="days"
                parentClasses="w-[74px] md:w-full"
              />
              <AppSelect
                :mode="mode"
                title="Type"
                parentClasses="w-[100px] md:w-full"
                v-model="record_type"
              >
                <template #options>
                  <option value="All">All</option>
                  <option value="L">Listing</option>
                  <option value="M">Mail</option>
                </template>
              </AppSelect>
            </div>
            <div class="mt-6 sm:mt-4">
              <AppButton
                title="PowerSearch"
                :isDisabled="!query || !days"
                @action="search"
              />
              <p
                class="mt-3 text-[12px] font-medium"
                :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
              >
                We have put a default value of 30 days in the search above so
                that you will see listings starting 30 days ago. If you click on
                Post Date below, you can change the listing from OLDEST to
                NEWEST or visa versa.
              </p>
            </div>
          </template>
        </AppCard>
      </div>
      <AppCard
        :mode="mode"
        title="Save this Search"
        :isSmallTitle="true"
        :classes="'h-full md:h-auto'"
      >
        <template #body>
          <div class="mdmd:gap-4 grid gap-6">
            <AppInput
              :mode="mode"
              id="name"
              title="Name your search"
              placeholder="e.g. Cisco Switches"
              v-model="queryToSave"
            />
            <div>
              <AppButton
                title="Save search"
                :isDisabled="!queryToSave"
                @action="saveSearch"
                type="secondary"
              />
            </div>
          </div>
          <div class="mt-3">
            <p
              class="text-[12px] font-medium"
              :class="[mode === 'dark' ? 'text-dark-text' : 'text-black-60']"
            >
              You can view and edit your saved searches later.
            </p>
            <div class="mt-2">
              <RouterLink
                :to="{ name: 'searches' }"
                class="block text-[12px] font-semibold text-primary-100 underline"
                >View and Edit Searches</RouterLink
              >
            </div>
          </div>
        </template>
      </AppCard>
    </div>
    <div v-if="searches.length > 0" class="mt-[60px] sm:mt-[40px]">
      <AppCard :mode="mode" title="Search Results">
        <template #body>
          <template v-if="paginatedItems.length > 0">
            <AppTable :mode="mode" :columns="columns">
              <template #rows>
                <tr v-for="(item, key) in paginatedItems" :key="key">
                  <td class="align-top">
                    <FallbackName
                      :to="{
                        name: 'contact-details',
                        params: {
                          id: item._source.contact.contact_id,
                        },
                      }"
                      :first="item._source.contact.first_name"
                      :last="item._source.contact.last_name"
                    />
                  </td>
                  <td class="align-top">
                    <FallbackDateTime
                      :mode="mode"
                      :value="item._source.powersearch.post_date"
                    />
                  </td>
                  <td class="align-top">
                    <FallbackDetails
                      :mode="mode"
                      :value="item._source.powersearch"
                    />
                  </td>
                </tr>
              </template>
            </AppTable>
            <MobileTable
              :mode="mode"
              :fields="{
                name: 'Name',
                date: 'Date',
                details: 'Details',
              }"
              :data="paginatedItems"
            >
              <template #default="{ row, fieldKey }">
                <FallbackName
                  v-if="fieldKey === 'name'"
                  :to="{
                    name: 'contact-details',
                    params: {
                      id: row._source.contact.contact_id,
                    },
                  }"
                  :first="row._source.contact.first_name"
                  :last="row._source.contact.last_name"
                  :isTruncate="false"
                />
                <template v-else-if="fieldKey === 'date'">
                  <FallbackDateTime
                    :mode="mode"
                    :value="row._source.powersearch.post_date"
                  />
                </template>
                <template v-else-if="fieldKey === 'details'">
                  <FallbackDetails
                    :mode="mode"
                    :value="row._source.powersearch"
                  />
                </template>
              </template>
            </MobileTable>
          </template>
          <AppPagination
            :mode="mode"
            :data="searches"
            @pageChanged="updatePaginatedItems"
          />
        </template>
      </AppCard>
    </div>
  </div>
</template>

<script>
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useLoaderStore } from "@S/loader";
import { useToastStore } from "@S/toast";
import { useUserStore } from "@S/user";
import AppCard from "@C/Common/AppCard.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppTable from "@C/Tables/AppTable.vue";
import AppPagination from "@C/Common/AppPagination.vue";
import FallbackName from "@C/Tables/FallbackName.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import FallbackDetails from "@C/Tables/FallbackDetails.vue";
import FallbackDateTime from "@C/Tables/FallbackDateTime.vue";
import AppButton from "@C/Common/AppButton.vue";

export default {
  name: "SearchForm",
  props: {
    single: {
      type: Boolean,
      default: false,
    },
    mode: String,
  },
  components: {
    AppCard,
    AppInput,
    AppSelect,
    AppTable,
    AppPagination,
    FallbackName,
    MobileTable,
    FallbackDetails,
    FallbackDateTime,
    AppButton,
  },
  setup() {
    const loaderStore = useLoaderStore();
    const toastStore = useToastStore();
    const userStore = useUserStore();
    const { handleError } = useGlobalErrorHandler();
    return { loaderStore, toastStore, userStore, handleError };
  },
  data() {
    return {
      query: "",
      queryToSave: "",
      days: 30,
      record_type: "All",
      searches: [],
      id: "",
      savedSearch: null,
      columns: [
        {
          name: "Name",
        },
        {
          name: "Date",
        },
        {
          name: "Details",
        },
      ],
      paginatedItems: [],
    };
  },
  created() {
    if (this.single) {
      this.id = this.$route.params.id;
    }
  },
  mounted() {
    if (this.single) {
      this.getSearch();
    }
    
    // Check if there's a search query in the URL
    const queryParam = this.$route.query.q;
    if (queryParam) {
      this.query = queryParam;
      this.search();
    }
  },
  methods: {
    async search() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        query: this.query,
        days: this.days,
        record_type: this.record_type,
      };
      if (this.record_type === "All") {
        delete payload.record_type;
      }
      this.currentPage = 1;
      try {
        const { data } = await ApiRequest().post(`/powersearch`, payload);
        if (data) {
          this.searches = data;
        } else {
          this.searches = [];
          this.toastStore.show("No search results found", "info");
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async getSearch() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/contact_searches/${this.id}`);
        if (data) {
          this.savedSearch = data;
          this.query = data.contact_search.query;
          this.queryToSave = data.contact_search.search_name;
          this.days = data.contact_search.days_to_search;
        } else {
          this.toastStore.show("Search not found", "error");
          this.$router.push({ name: "search" });
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async saveSearch() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        contact_search: {
          contact_id: this.single
            ? this.savedSearch.contact_search.contact_id
            : this.userStore.user.contact.contact_id,
          contact_search_id: this.single
            ? this.savedSearch.contact_search.contact_search_id
            : "",
          days_to_search: this.days,
          is_daily_summary: false,
          is_data_feed: false,
          last_sent: "2017-09-27T13:46:23.517000",
          query: this.query,
          search_name: this.queryToSave,
          send_time_hr: 18,
          send_time_min: 0,
        },
      };
      try {
        const { data } = await ApiRequest().post(`/contact_searches`, payload);
        if (data) {
          this.toastStore.show("Search saved successfully!", "success");
          this.$router.push({ name: "searches" });
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    updatePaginatedItems(paginatedData) {
      this.paginatedItems = paginatedData;
    },
  },
};
</script>
